import {
  Url,
  uid,
  dashify,
  destroyObject,
  determineAppLanguage
} from "app/utils";
import { Iframe, MessageBus } from "app/classes";
import { NavService, SpinnerService, $Window } from "app/services";
import { ApplicationEvents } from "./application.events";
import { $CurrentUser } from "app/classes/current.user/current.user";
import { SubRegionMapper } from "./application.sub.regionMap";
import * as moment from "moment-timezone";

export interface ISubscription {
  application: Application;
  provisioned_date: string;
  application_id: string;
  organization_id: string;
  provisioned_user_id: string;
  status: string;
  updated_by_id: string;
  updated_date: string;
  acctServPhoneNumber?: string;
  acctPhoneOption?: string;
  acctServMessage?: string;
  acctIsAPAC: boolean;
}

export class Application {
  // Properties from Identity Object
  public id: string = uid();
  public roles: Array<any>;
  public publisher_org_id: string = "";
  public publisher_user_id: string = "";
  public name: string = "";
  public slug: string = "";
  public slug_archive: string = "";
  public url: string = "";
  public uses_seat_licensing: boolean = false;
  public version: string = "";
  public status: string = "";
  public description: string = "";
  public logo: string = "";
  public namespace: string = "";
  public created_at: string = "";
  public updated_at: string = "";
  public maintenance_url: string = "";
  public in_maintenance_mode: boolean = false;
  public is_service: boolean = false;
  public hardcoded?: boolean = false;

  // default language to english
  public language_list: Array<string> = ["en-US"];
  public language_version: string = "";
  public launchpad_display: boolean = false;
  public is_archived: boolean = false;
  public is_public: boolean = false;

  // Custom Properties
  public subscription?: ISubscription;
  public bus: MessageBus;
  public iframe: Iframe;
  public baseUrl: string = "";
  public readyResolve: () => void;
  private ready: Promise<any>;

  // Application state
  public showing: boolean = false;
  public loaded: boolean = false;

  // Application settings
  public nav: boolean = true; // The blue bar should display with this app
  public public: boolean = false; // App can be displayed without login
  public preload: boolean = false; // App can be preloaded before the user is logged in
  public passToken: boolean = false; // App needs the token passed in the iframe url
  public background: boolean = false; // App can be run in the background behind other apps
  public privileged: boolean = false; // Access is allowed by user roles
  public showSpinner: boolean = false; // Show spinner while loading

  constructor(opts: any) {
    Object.keys(opts).forEach(key => {
      this[key] = opts[key];
    });

    this.slug = this.slug || dashify(this.name);
    this.baseUrl = Url.wrapSlashes(Url.join($Window.baseUrl, this.slug));
    this.ready = new Promise(resolve => {
      this.readyResolve = resolve;
    });
    if (this.subscription && this.subscription.status !== "active") {
      const timezone =
        $CurrentUser.preferences && $CurrentUser.preferences.timezone
          ? $CurrentUser.preferences.timezone
          : moment.tz.guess();
      const contactData = new SubRegionMapper(timezone).getContactData(
        this.subscription.status
      );
      this.subscription.acctServPhoneNumber = contactData.phone;
      this.subscription.acctPhoneOption = contactData.option;
      this.subscription.acctIsAPAC = contactData.isAPAC;
      this.subscription.acctServMessage = contactData.message;
    }
  }

  public details() {
    const obj: any = {};
    const ignore = ["bus", "iframe"];
    for (const key in this) {
      if (this.hasOwnProperty(key)) {
        const isIgnored = ignore.indexOf(key) >= 0;
        const isFunction = typeof this[key] === "function";
        obj[key] = isIgnored || isFunction ? "" : this[key];
      }
    }
    return obj;
  }

  public destroy = () => {
    destroyObject(this);
  };

  public whenReady = () => {
    return this.ready;
  };

  public launch = (path = "") => {
    if (this.loaded) {
      this.iframe.changeHashPath(path, this.language_list);
    } else {
      this.load(path);
    }
    this.show();
    return this.ready;
  };

  /**
   * When CP loads an iframe, first hide the spinner.
   * This covers an edge case when an application loads
   * and either:
   *    1) The application fails before it sends the
   *       message to CP to hide the spinner.
   *    2) The user navigates away from the app, before it
   *       sends the message to CP to hide the spinner.
   *
   * If CP never received the hide spinner message, then
   * the spinner will continue to show, even when the user
   * navigates to another application, It is possible that
   * when that application loads, it will call eventually
   * send the messgae to CP to hide the spinner, but that
   * is not gauranteed.
   */
  private handleSpinner() {
    SpinnerService.hide();
    if (this.showSpinner) {
      SpinnerService.show();
    }
  }

  public load = (path = "") => {
    if (this.loaded) {
      return false;
    }
    this.handleSpinner();

    this.iframe = new Iframe(this.slug, this.buildUrl(path));
    this.iframe.load();

    this.bus = new MessageBus(this.iframe.window);
    this.bus.allowOrigin(Url.parse(this.url).origin);

    const appEvents = new ApplicationEvents(this, this.bus);
    appEvents.registerAll();

    this.loaded = true;
    return true;
  };

  public buildUrl = (path: string) => {
    const urlObj = Url.parse(this.url);
    urlObj.hash = "#" + path;
    if (this.passToken && $CurrentUser.token) {
      const searchObj = Url.parseSearch(urlObj.search);
      searchObj.access_token = $CurrentUser.token;
      searchObj.lang = determineAppLanguage(
        this.language_list,
        $CurrentUser.preferences.language
      );
      urlObj.search = searchObj;
    }
    return Url.build(urlObj);
  };

  public show = () => {
    if (!this.loaded) {
      return false;
    }
    (this.nav ? NavService.show : NavService.hide)();
    $Window.setTitle(this.name);
    this.iframe.show();
    this.showing = true;
    return true;
  };

  public hide = () => {
    if (!this.loaded) {
      return false;
    }
    if (this.background) {
      this.iframe.hide();
    } else {
      this.close();
    }
    this.showing = false;
    return true;
  };

  public close = () => {
    if (!this.loaded) {
      return false;
    }
    this.iframe.destroy();
    this.bus.destroy();
    delete this.bus;
    delete this.iframe;
    this.loaded = false;
  };
}
